import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const ChatPanel = ({ interviewId, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (!inputValue?.trim()) return;

    const newMessage = {
      id: Date.now(),
      senderId: user?.id,
      content: inputValue,
      timestamp: new Date(),
      isOwn: true
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');

    // In production, send via Supabase realtime
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="w-80 bg-[#1F2937] border-l border-[#374151] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-[#374151] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="MessageSquare" size={20} color="#FFFFFF" />
          <h3 className="text-white font-semibold">Chat</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-[#374151] rounded-lg transition-colors"
          aria-label="Close chat"
        >
          <Icon name="X" size={20} color="#9CA3AF" />
        </button>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages?.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-[#374151] rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="MessageCircle" size={24} color="#9CA3AF" />
            </div>
            <p className="text-gray-400 text-sm">No messages yet</p>
            <p className="text-gray-500 text-xs mt-1">Start the conversation</p>
          </div>
        ) : (
          messages?.map((message) => (
            <div
              key={message?.id}
              className={`flex ${message?.isOwn ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 ${
                  message?.isOwn
                    ? 'bg-blue-600 text-white' :'bg-[#374151] text-gray-200'
                }`}
              >
                <p className="text-sm break-words">{message?.content}</p>
                <p
                  className={`text-xs mt-1 ${
                    message?.isOwn ? 'text-blue-200' : 'text-gray-400'
                  }`}
                >
                  {formatTime(message?.timestamp)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Input */}
      <div className="p-4 border-t border-[#374151]">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e?.target?.value)}
            placeholder="Type a message..."
            className="flex-1 bg-[#374151] text-white placeholder-gray-400 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
          <button
            type="submit"
            disabled={!inputValue?.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-[#374151] disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 transition-colors"
            aria-label="Send message"
          >
            <Icon name="Send" size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPanel;